/**
 * Formatting Utilities
 *
 * Common formatting functions for the application.
 *
 * @see US-2.5.5 - Multi-Currency Support
 */

import type { Currency } from '@bstock/shared';

/**
 * Formate une quantite avec 2 decimales.
 * Uniforme pour toutes les unites (masse, volume, piece).
 */
export function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return value.toFixed(2);
}

/**
 * Retourne le step pour les inputs de quantite.
 * Toujours 0.1 (incrementer par dixiemes, centiemes saisissables manuellement).
 */
export function getQuantityStep(): string {
  return '0.1';
}

/**
 * Formats a price amount with the specified currency.
 * Uses Intl.NumberFormat with 'fr-FR' locale for consistent formatting.
 *
 * @param amount - The price amount (can be null)
 * @param currency - ISO 4217 currency code (defaults to 'EUR')
 * @returns Formatted price string or '-' if amount is null
 *
 * @example
 * formatPrice(1234.56) // "1 234,56 EUR"
 * formatPrice(1234.56, 'CHF') // "1 234,56 CHF"
 * formatPrice(null) // "-"
 */
export function formatPrice(amount: number | null, currency: Currency | string = 'EUR'): string {
  if (amount === null || amount === undefined) {
    return '-';
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

/**
 * Formats a price amount stored in centimes (integer) to currency string.
 * This is useful for amounts stored in the database as integers to avoid
 * floating-point precision issues.
 *
 * @param centimes - The price amount in centimes (e.g., 1234 = 12.34 EUR)
 * @param currency - ISO 4217 currency code (defaults to 'EUR')
 * @returns Formatted price string or '-' if amount is null
 *
 * @example
 * formatCentimes(1234) // "12,34 €"
 * formatCentimes(1234, 'CHF') // "CHF 12.34"
 * formatCentimes(null) // "-"
 */
export function formatCentimes(centimes: number | null, currency: Currency | string = 'EUR'): string {
  if (centimes === null || centimes === undefined) {
    return '-';
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
  }).format(centimes / 100);
}


